import { useLocalStorage } from '@/hooks/use-local-storage';
import { useEffect, useRef, useState } from 'react';

const RIGHT_SIDEBAR_MIN_WIDTH = 400;
const RIGHT_SIDEBAR_MAX_WIDTH = 600;
const RIGHT_SIDEBAR_SNAP_COLLAPSE_WIDTH = 440;
const RIGHT_SIDEBAR_SNAP_ANIMATION_MS = 300;

export function useReportSidebar(completionAttempt: number) {
    const [sidebarWidth, setSidebarWidth] = useLocalStorage('create_report_sidebar_width', 480);
    const [isResizing, setIsResizing] = useState(false);
    const [isLeftSidebarCollapsed, setIsLeftSidebarCollapsed] = useLocalStorage(
        'create_report_left_sidebar_collapsed',
        false,
    );
    const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useLocalStorage(
        'create_report_right_sidebar_collapsed',
        false,
    );
    const [isSnappingRightSidebar, setIsSnappingRightSidebar] = useState(false);
    const prevCompletionAttempt = useRef(completionAttempt);
    const snapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const resizeStartXRef = useRef<number | null>(null);
    const didResizeMoveRef = useRef(false);

    useEffect(() => {
        if (completionAttempt > prevCompletionAttempt.current && completionAttempt > 0) {
            setIsLeftSidebarCollapsed(false);
        }

        prevCompletionAttempt.current = completionAttempt;
    }, [completionAttempt, setIsLeftSidebarCollapsed]);

    const isSidebarExpanded = !isLeftSidebarCollapsed;
    const isRightSidebarExpanded = !isRightSidebarCollapsed;

    const handleRightSidebarResizeStart = (clientX: number) => {
        if (!isRightSidebarExpanded) return;

        resizeStartXRef.current = clientX;
        didResizeMoveRef.current = false;
        setIsResizing(true);
    };

    useEffect(() => {
        return () => {
            if (snapTimeoutRef.current) {
                clearTimeout(snapTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isRightSidebarExpanded && isResizing) {
            setIsResizing(false);
        }
    }, [isRightSidebarExpanded, isResizing]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return;

            if (
                resizeStartXRef.current !== null &&
                Math.abs(e.clientX - resizeStartXRef.current) > 3
            ) {
                didResizeMoveRef.current = true;
            }

            const newWidth = window.innerWidth - e.clientX;
            if (newWidth >= RIGHT_SIDEBAR_MIN_WIDTH && newWidth <= RIGHT_SIDEBAR_MAX_WIDTH) {
                setSidebarWidth(newWidth);
            }
        };

        const handleMouseUp = (e: MouseEvent) => {
            if (isResizing && isRightSidebarExpanded && didResizeMoveRef.current) {
                const finalWidth = window.innerWidth - e.clientX;

                if (finalWidth <= RIGHT_SIDEBAR_SNAP_COLLAPSE_WIDTH) {
                    setIsRightSidebarCollapsed(true);
                    setIsSnappingRightSidebar(true);

                    if (snapTimeoutRef.current) {
                        clearTimeout(snapTimeoutRef.current);
                    }

                    snapTimeoutRef.current = setTimeout(() => {
                        setIsSnappingRightSidebar(false);
                        snapTimeoutRef.current = null;
                    }, RIGHT_SIDEBAR_SNAP_ANIMATION_MS);
                }
            }

            setIsResizing(false);
            resizeStartXRef.current = null;
            didResizeMoveRef.current = false;
        };

        if (isResizing && isRightSidebarExpanded) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, isRightSidebarExpanded, setSidebarWidth, setIsRightSidebarCollapsed]);

    return {
        sidebarWidth,
        isResizing,
        isLeftSidebarCollapsed,
        setIsLeftSidebarCollapsed,
        isSidebarExpanded,
        isRightSidebarCollapsed,
        setIsRightSidebarCollapsed,
        isRightSidebarExpanded,
        isSnappingRightSidebar,
        handleRightSidebarResizeStart,
    };
}
